import { useMemo } from "react";
import { Alert } from "antd";
import { LoadingOutlined, CloseCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useLookupSyncStatus } from "../../hooks/useLookupSyncStatus";

const HIDE_DELAY_MS = 4000; // Hiding the bbanner after 4 secs

const formatLookupList = (items = [], emptyText = "") => {
  if (!items.length) {
    return emptyText;
  }

  return items
    .slice(0, 3)
    .map((item) => item.label || item.store)
    .join(", ") + (items.length > 3 ? ` +${items.length - 3} more` : "");
};

const getAlertConfig = (snapshot) => {
  const phase = snapshot?.phase || "idle";
  const failedCount = snapshot?.failed?.length || 0;
  const syncedCount = snapshot?.synced?.length || 0;
  const skippedCount = snapshot?.skipped?.length || snapshot?.skippedStores?.length || 0;
  const queuedText = formatLookupList(snapshot?.queued);
  const syncingText = formatLookupList(snapshot?.syncing);
  const totalCount = snapshot?.statuses?.length || syncedCount + failedCount + skippedCount;

  if (phase === "queued") {
    return {
      type: "info",
      icon: <ClockCircleOutlined />,
      message: "Preparing offline dropdown data",
      description: queuedText
        ? `${totalCount} lookup lists queued for offline sync. Next: ${queuedText}.`
        : `${totalCount} lookup lists queued for offline sync.`,
      closable: false,
    };
  }

  if (phase === "syncing") {
    return {
      type: "info",
      icon: <LoadingOutlined />,
      message: "Syncing offline dropdown data",
      description: syncingText
        ? `${syncedCount + failedCount}/${totalCount} lookup lists processed. In progress: ${syncingText}.`
        : `${syncedCount + failedCount}/${totalCount} lookup lists processed.`,
      closable: false,
    };
  }

  if (phase === "skipped") {
    return {
      type: "warning",
      icon: <ClockCircleOutlined />,
      message: "Offline lookup sync skipped",
      description: snapshot?.reason === "offline"
        ? "Lookup preload will run again when you are back online."
        : "Lookup preload was skipped.",
      closable: true,
    };
  }

  if (phase === "completed" && failedCount > 0) {
    return {
      type: "warning",
      icon: <CloseCircleOutlined />,
      message: "The app data is not available for offline use. Please try refreshing the app. If the issue persists, please contact Helpdesk helpdesk@dsti.gov.za.",
      description: null,
      closable: true,
    };
  }

  if (phase === "completed") {
    return null;
  }

  return null;
};

const LookupSyncBanner = () => {
  const { snapshot, visible, setVisible } = useLookupSyncStatus({
    autoHideDelayMs: HIDE_DELAY_MS,
  });

  const alertConfig = useMemo(() => getAlertConfig(snapshot), [snapshot]);
  const hasFailures = (snapshot?.failed?.length || 0) > 0;
  const bannerClassName = `lookup-sync-banner lookup-sync-banner--${snapshot?.phase}${hasFailures ? " lookup-sync-banner--failed" : ""}`;

  if (!visible || !alertConfig) {
    return null;
  }

  return (
    <div className="lookup-sync-banner-container">
      <Alert
        banner
        showIcon
        className={bannerClassName}
        message={alertConfig.message}
        description={alertConfig.description}
        type={alertConfig.type}
        icon={alertConfig.icon}
        closable={alertConfig.closable}
        onClose={() => setVisible(false)}
      />
    </div>
  );
};

export default LookupSyncBanner;